import React, { ChangeEvent, useCallback } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import ModalDialog from '../../../common/components/ModalDialog';
import { useSnapshot } from 'valtio';
import PoliciesProfileUIStateStore from '../../store/policies-profile-ui';
import { setShowSaveAsModal } from '../../actions/policies-selection-ui-actions/policies-selection-ui-actions';
import { MdCreate as CreateIcon } from 'react-icons/md';
import { LoadingButton } from '@mui/lab';
import CustomTooltip from '../../../common/components/CustomTooltip';
import { MdSave as SaveAsIcon } from 'react-icons/md';
import ProfileStore from '../../store/profiles';
import { editNewProfile } from '../../actions/profiles-actions/profile-state-actions';
import saveAsNewProfileAction from '../../actions/profiles-actions/save-as-new-profile-action';

interface SaveAsNewProfileModalProps {
  onClose?: () => void;
}
const SaveAsNewProfileModal: React.FC<
  React.PropsWithChildren<SaveAsNewProfileModalProps>
> = () => {
  const theme = useTheme();
  const { showSaveAsModal, createNewProfileInProgress } = useSnapshot(
    PoliciesProfileUIStateStore,
  );
  const {
    newProfile: {
      payload: { profileName, active },
      responseErorMessage,
    },
  } = useSnapshot(ProfileStore);

  const onProfileNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      editNewProfile(event.target.name, event.target.value);
    },
    [],
  );

  const onActiveChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      editNewProfile(event.target.name, checked);
    },
    [],
  );

  return (
    <ModalDialog
      modalColor={theme.palette.info.main}
      aria-label='Save As'
      open={showSaveAsModal}
      onClose={() => {
        setShowSaveAsModal(false);
      }}
      modalHeader='Save as new profile'>
      <Box
        display='flex'
        justifyContent='space-between'
        flexDirection='column'
        gap={2}
        alignItems='flex-end'>
        <TextField
          error={!!responseErorMessage}
          placeholder='New profile name'
          size='small'
          required
          variant='outlined'
          label='Enter new profile name'
          name='profileName'
          fullWidth
          value={profileName}
          onChange={onProfileNameChange}
          disabled={false}
          helperText={responseErorMessage}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {!!!profileName && (
                  <CreateIcon
                    fontSize='small'
                    style={{ fill: theme.palette.info.light }}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />
        <Stack direction='row' spacing={1}>
          <CustomTooltip title='Set profile as active'>
            <FormControlLabel
              value={active}
              control={
                <Checkbox
                  name='active'
                  size='small'
                  color='info'
                  inputProps={{ 'aria-label': 'Set active' }}
                  checked={active}
                  onChange={onActiveChange}
                />
              }
              label='Active'
              labelPlacement='end'
            />
          </CustomTooltip>
          <CustomTooltip title={`Save changes to profile ${profileName}`}>
            <span>
              <LoadingButton
                loading={createNewProfileInProgress}
                loadingPosition='center'
                size='medium'
                variant='outlined'
                color='primary'
                onClick={saveAsNewProfileAction}
                disabled={!(profileName.split('').length > 3)}
                startIcon={<SaveAsIcon fontSize='small' />}>
                Save
              </LoadingButton>
            </span>
          </CustomTooltip>
        </Stack>
      </Box>
    </ModalDialog>
  );
};

export default SaveAsNewProfileModal;
